import { DataTable, type LocalDataColumn } from "@nymbl/nos2-rules";
import { useMemo } from "react";
import type { EntryFilter } from "@/components/team/panel-state";
import { TypeChip } from "@/components/time/TypeChip";
import { usePersonEntries } from "@/hooks/use-team";
import { PERSON_ENTRIES_HELP } from "@/lib/column-help";
import { TIME_ENTRY_TYPE_OPTIONS } from "@/lib/enums";
import { withCells } from "@/lib/table-columns";
import { formatHours } from "@/lib/team/metrics";
import { monthParam } from "@/lib/team/months";
import { formatEntryDate, type IsoDate } from "@/lib/time/dates";
import type { PersonEntry } from "@/schemas/team";

export const PERSON_ENTRIES_COLUMNS: LocalDataColumn<PersonEntry>[] = [
  {
    key: "date",
    label: "Date",
    type: "date",
    accessor: (e) => e.date,
    help: PERSON_ENTRIES_HELP.date,
  },
  {
    key: "task",
    label: "Task",
    type: "text",
    accessor: (e) => e.taskName,
    searchText: (e) => [e.demandName, e.projectName].filter(Boolean).join(" "),
    help: PERSON_ENTRIES_HELP.task,
  },
  {
    key: "type",
    label: "Type",
    type: "enum",
    accessor: (e) => e.type,
    options: [...TIME_ENTRY_TYPE_OPTIONS],
    help: PERSON_ENTRIES_HELP.type,
  },
  {
    key: "hours",
    label: "Hours",
    type: "number",
    accessor: (e) => e.hours,
    help: PERSON_ENTRIES_HELP.hours,
  },
  {
    key: "comment",
    label: "Comment",
    type: "text",
    accessor: (e) => e.comment,
    help: PERSON_ENTRIES_HELP.comment,
  },
];

const COLUMNS = withCells(PERSON_ENTRIES_COLUMNS, {
  date: { render: (e) => formatEntryDate(e.date) },
  task: {
    className: "min-w-56",
    render: (e) => (
      <>
        <span className="font-semibold">{e.taskName}</span>
        <div className="text-xs text-muted-foreground">{e.demandName ?? e.projectName ?? ""}</div>
      </>
    ),
  },
  type: { render: (e) => <TypeChip type={e.type} /> },
  hours: { render: (e) => formatHours(e.hours) },
  comment: { className: "min-w-48", render: (e) => e.comment ?? "" },
});

const byTask = (a: PersonEntry, b: PersonEntry) => a.taskName.localeCompare(b.taskName);

interface PersonEntriesTabProps {
  personId: string;
  month: IsoDate;
  active: boolean;
  /** A drill-down's type (e.g. billable), which opens the table filtered to it. */
  filter: EntryFilter;
}

export function PersonEntriesTab({ personId, month, active, filter }: PersonEntriesTabProps) {
  const query = usePersonEntries(personId, monthParam(month), active);
  const rows = useMemo(() => [...(query.data ?? [])].sort(byTask), [query.data]);

  return (
    <section aria-label="Time entries">
      <DataTable
        key={filter}
        initialFilters={
          filter === "all" ? undefined : { type: { type: "values", values: [filter] } }
        }
        label="Time entries"
        search={{ placeholder: "Search entries…" }}
        exportName="person-entries"
        columns={COLUMNS}
        rows={rows}
        rowKey={(e) => e.id}
        defaultSort="date"
        defaultSortDir="desc"
        loading={query.isPending}
        error={query.isError ? new Error("Didn't load") : undefined}
        onRetry={() => void query.refetch()}
        empty={{ title: "No entries" }}
      />
    </section>
  );
}
